import React, { useMemo } from 'react';

import { ChipsList } from '@/components/ChipsList';
import { useCellFilters } from '@/sections/asset/components/cells/CellFiltersContext';
import { getPrioritizedTags } from '@/sections/asset/components/cells/tagsCell.util';
import type { Asset } from '@/types';

interface TagsCellProps {
  asset: Asset;
}

/**
 * Memoized cell renderer for tags column to improve performance
 *
 * Features:
 * - Shows first tag with "+N" counter for additional tags
 * - Hovering anywhere in the cell shows all tags in tooltip
 * - Filtered tags appear first when tag filters are active
 * - Matches the interaction pattern of origins and attack surface columns
 *
 * @param asset - The asset to render tags for
 * @returns JSX element with tag chips or dash if no tags
 */
export const TagsCell: React.FC<TagsCellProps> = React.memo(({ asset }) => {
  // Access filter context for intelligent chip prioritization
  const filters = useCellFilters();
  const activeTagFilters = filters?.advancedFilters?.tags;

  const tags = asset.tags || [];

  // Apply filter-based prioritization
  const prioritizedTags = useMemo(
    () => getPrioritizedTags(tags, activeTagFilters),
    [tags, activeTagFilters]
  );

  if (!tags.length) return <>-</>;

  return (
    <ChipsList
      items={prioritizedTags}
      maxVisible={1} // Show first tag + counter for remaining
      variant="filter" // User-defined tags use filter variant
      size="xs"
      data-testid="asset-tags"
      wrapInTooltip // Hover anywhere in cell to see all tags
    />
  );
});

TagsCell.displayName = 'TagsCell';
